import { type ButtonHTMLAttributes, forwardRef } from 'react';
import { clsx } from 'clsx';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost' | 'outline' | 'dark';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'primary', size = 'md', className, children, loading, disabled, ...props }, ref) => {
    return (
      <button
        ref={ref}
        disabled={disabled || loading}
        className={clsx(
          'relative inline-flex items-center justify-center rounded-full font-medium tracking-[-0.01em] transition-colors duration-150 select-none cursor-pointer',
          'disabled:opacity-40 disabled:cursor-not-allowed',
          {
            'bg-primary text-on-primary hover:bg-primary-hover': variant === 'primary',
            'bg-dark-elevated text-dark-text border border-dark-border hover:border-[#303036] hover:bg-dark-hover': variant === 'secondary',
            'bg-[#040404] text-white border border-[#040404] hover:bg-[#1c1c1f]': variant === 'dark',
            'bg-transparent text-danger border border-danger/30 hover:bg-danger/10': variant === 'danger',
            'text-dark-muted hover:text-dark-text hover:bg-dark-hover': variant === 'ghost',
            'border border-dark-border text-dark-text hover:border-primary hover:text-primary': variant === 'outline',
          },
          {
            'h-8 px-3.5 text-[13px] gap-1.5': size === 'sm',
            'h-10 px-5 text-sm gap-2': size === 'md',
            'h-12 px-6 text-[15px] gap-2': size === 'lg',
          },
          className,
        )}
        {...props}
      >
        {loading && <span className="mr-2 h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />}
        {children}
      </button>
    );
  },
);
Button.displayName = 'Button';
